// The copy-import review screen (slice 1, design settled with Edward
// 2026-10-09): upload the client's one document (Word doc or campaign
// spreadsheet), see the PROPOSED copy blocks the parser found, map each onto
// one of the project's assets (or create a linked asset from a URL row, or
// skip it), then apply — confirmed blocks land as per-asset rich client
// copy exactly as if pasted. One sheet imports into one project; detected
// lead-form questions APPEND to Questions & privacy. Nothing is written
// until Apply, and existing copy is never overwritten without its tick.
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  FONTS, INK, NEUTRAL, HUES, STATE, RADII, card, displayFont,
  buttonDisabled, buttonPrimary, buttonSmall,
} from '../../utils/wizardTheme';
import { sanitizeCopyHtml } from '../../utils/richCopy';
import FileDropZone from '../../components/FileDropZone';

const API = process.env.REACT_APP_API_URL;

const selectStyle = {
  padding: '7px 10px', fontSize: '12.5px', fontFamily: FONTS.body,
  border: `1px solid ${NEUTRAL.controlSoft}`, borderRadius: RADII.small,
  background: NEUTRAL.pageBg, maxWidth: '100%',
};

export default function CopyImportPanel({ projectId, onClose, onApplied }) {
  const [phase, setPhase] = useState('pick'); // pick | review | done
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [sheetName, setSheetName] = useState(null);
  const [mapping, setMapping] = useState({}); // blockKey -> {mode, assetId, overwrite}
  const [includeQuestions, setIncludeQuestions] = useState(true);
  const [result, setResult] = useState(null);

  const sheets = preview?.kind === 'sheet' ? preview.sheets : null;
  const importable = (sheets || []).filter(s => s.importable);
  const blocks = useMemo(() => {
    if (!preview) return [];
    if (preview.kind === 'document') return preview.blocks;
    return (importable.find(s => s.name === sheetName) || importable[0] || { blocks: [] }).blocks;
  }, [preview, sheetName, importable]);
  const assets = useMemo(() => preview?.assets || [], [preview]);
  const assetById = useMemo(() => Object.fromEntries(assets.map(a => [String(a.id), a])), [assets]);

  // A Word doc's "Landing page:" URL (the SailPoint shape, 2026-10-28) is a
  // hint, not proof the asset is missing: on a project that already has its
  // files, an unmatched block waits for the PM rather than defaulting to a
  // new linked asset (a likely duplicate). Spreadsheet URL rows keep the
  // create default.
  const urlCreates = (b) => b.url_hint && (preview?.kind !== 'document' || assets.length === 0);
  const defaultFor = (b) => (
    b.suggested_asset_id
      ? { mode: 'asset', assetId: String(b.suggested_asset_id), overwrite: false }
      : urlCreates(b) ? { mode: 'create', assetId: '', overwrite: false }
        // No assets on the project yet = the import-to-find-assets flow: a
        // titled block defaults to creating its asset, not to a skip the PM
        // can't escape (Edward's staging report, 2026-10-15: every block stuck
        // on "don't import", 0 assets imported).
        : assets.length === 0 && (b.title || b.file_hint)
          ? { mode: 'create', assetId: '', overwrite: false }
          : { mode: 'skip', assetId: '', overwrite: false }
  );
  const entryFor = (b) => mapping[b.key] || defaultFor(b);
  const setEntry = (key, patch) => setMapping(m => ({ ...m, [key]: { ...(m[key] || {}), ...patch } }));

  const upload = async (files) => {
    const file = files && files[0];
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/contentiq/projects/${projectId}/copy-import/preview`, fd);
      setPreview(r.data);
      setMapping({});
      if (r.data.kind === 'sheet') {
        const first = (r.data.sheets || []).find(s => s.importable);
        if (!first) { setErr('No sheet in that file has Headline/Abstract copy columns.'); setBusy(false); return; }
        setSheetName(first.name);
      } else if (!(r.data.blocks || []).length && !r.data.questions_html) {
        setErr('No copy blocks were found in that document.'); setBusy(false); return;
      }
      setPhase('review');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not read that file.');
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true); setErr('');
    try {
      const payload = { blocks: [], questions_html: null };
      for (const b of blocks) {
        const e = entryFor(b);
        if (e.mode === 'asset' && e.assetId) {
          payload.blocks.push({
            asset_id: Number(e.assetId), title: b.title,
            body_html: b.body_html, overwrite: !!e.overwrite,
          });
        } else if (e.mode === 'create' && b.url_hint) {
          payload.blocks.push({
            title: b.title, body_html: b.body_html,
            create_url: { title: b.title || b.file_hint || b.url_hint, url: b.url_hint },
          });
        } else if (e.mode === 'create') {
          // No URL in the block (Word docs never carry one): create the asset
          // from its title alone, copy attached.
          payload.blocks.push({
            title: b.title, body_html: b.body_html,
            create_title: b.title || b.file_hint,
          });
        }
      }
      if (preview?.kind === 'document' && preview.questions_html && includeQuestions) {
        payload.questions_html = preview.questions_html;
      }
      const r = await axios.post(`${API}/contentiq/projects/${projectId}/copy-import/apply`, payload);
      setResult(r.data);
      setPhase('done');
      if (onApplied) await onApplied(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not apply the import.');
    } finally { setBusy(false); }
  };

  const importCount = blocks.filter(b => entryFor(b).mode !== 'skip').length;
  const applyDisabled = busy || (importCount === 0 && !(includeQuestions && preview?.questions_html));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(19, 24, 67, 0.35)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ ...card, maxWidth: '860px', width: '100%', maxHeight: '88vh', overflowY: 'auto', padding: '22px 26px', display: 'flex', flexDirection: 'column', gap: '14px', fontFamily: FONTS.body }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ ...displayFont('18px', 700) }}>Import copy from the client's document</div>
          <div style={{ flexGrow: 1 }} />
          <button onClick={onClose} style={buttonSmall}>✕ Close</button>
        </div>

        {phase === 'pick' && (
          <>
            <div style={{ fontSize: '13px', color: INK.secondary, lineHeight: 1.55 }}>
              One Word doc or spreadsheet holding the assets and their copy — I'll break it
              into blocks and you confirm which block belongs to which asset before anything
              is saved. A spreadsheet imports one sheet into this project.
            </div>
            <FileDropZone accept=".docx,.xlsx,.csv" multiple={false} disabled={busy}
              label={busy ? 'Reading…' : 'Drop the client’s copy document here — or click to browse'}
              hint=".docx, .xlsx or .csv"
              onFiles={upload} />
          </>
        )}

        {phase === 'review' && (
          <>
            {sheets && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '12.5px', color: INK.secondary }}>
                  Campaign sheet:{' '}
                  <select value={sheetName || ''} onChange={e => { setSheetName(e.target.value); setMapping({}); }} style={selectStyle}>
                    {importable.map(s => <option key={s.name} value={s.name}>{s.name} ({s.blocks.length} blocks)</option>)}
                  </select>
                </label>
                {sheets.some(s => !s.importable) && (
                  <span style={{ fontSize: '11.5px', color: INK.muted }}>
                    Ignored (no copy columns): {sheets.filter(s => !s.importable).map(s => s.name).join(', ')}
                  </span>
                )}
              </div>
            )}

            <div style={{ fontSize: '12px', color: INK.secondary }}>
              {blocks.length} copy block{blocks.length === 1 ? '' : 's'} found — map each to an
              asset, or skip it. Nothing is saved until you apply.
            </div>

            {blocks.map(b => {
              const e = entryFor(b);
              const chosen = e.mode === 'asset' ? assetById[e.assetId] : null;
              return (
                <div key={b.key} style={{ border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.control, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                    <b style={{ fontSize: '13px' }}>{b.title || b.file_hint || 'Untitled block'}</b>
                    {b.file_hint && <span style={{ fontSize: '11px', color: INK.muted }}>{b.file_hint}</span>}
                    {b.language && <span style={{ fontSize: '11px', color: INK.muted }}>lang: {b.language}</span>}
                    {b.suggested_asset_id && e.mode === 'asset' && String(b.suggested_asset_id) === e.assetId && (
                      <span style={{ fontSize: '10.5px', color: STATE.done.text, background: STATE.done.tint, borderRadius: RADII.pill, padding: '1px 8px', fontWeight: 700 }}>
                        matched{b.confidence >= 0.99 ? ' · exact' : ''}
                      </span>
                    )}
                  </div>
                  {b.body_html ? (
                    <div style={{ fontSize: '12px', color: INK.body, background: NEUTRAL.pageBg, border: `1px solid ${NEUTRAL.borderSoft}`, borderRadius: RADII.small, padding: '8px 10px', maxHeight: '120px', overflowY: 'auto', lineHeight: 1.5 }}
                      dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(b.body_html) }} />
                  ) : (
                    <div style={{ fontSize: '11.5px', color: INK.muted }}>No body copy in this block.</div>
                  )}
                  {b.image_count > 0 && (
                    <div style={{ fontSize: '11.5px', color: STATE.gate.text }}>
                      ⚠ This block holds {b.image_count} embedded image{b.image_count === 1 ? '' : 's'} —
                      text inside an image can't be read, so anything it carries isn't in the copy above.
                      Check the original document and paste what's missing by hand.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select value={e.mode === 'asset' ? e.assetId : e.mode} style={selectStyle}
                      onChange={ev => {
                        const v = ev.target.value;
                        if (v === 'skip' || v === 'create') setEntry(b.key, { mode: v, assetId: '' });
                        else setEntry(b.key, { mode: 'asset', assetId: v });
                      }}>
                      <option value="skip">— don't import —</option>
                      {b.url_hint
                        ? <option value="create">＋ Create as linked asset ({b.url_hint.slice(0, 50)}…)</option>
                        : (b.title || b.file_hint)
                          ? <option value="create">＋ Create as new asset ("{(b.title || b.file_hint).slice(0, 50)}")</option>
                          : null}
                      {assets.map(a => (
                        <option key={a.id} value={String(a.id)}>
                          → {a.title || a.filename}{a.has_client_copy ? ' (already has copy)' : ''}
                        </option>
                      ))}
                    </select>
                    {chosen?.has_client_copy && (
                      <label style={{ fontSize: '12px', color: STATE.gate.text, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!e.overwrite}
                          onChange={ev => setEntry(b.key, { overwrite: ev.target.checked })} />{' '}
                        overwrite its existing copy
                      </label>
                    )}
                  </div>
                </div>
              );
            })}

            {preview?.kind === 'document' && preview.questions_html && (
              <div style={{ border: `1px solid ${HUES.strategy.border}`, background: HUES.strategy.tint, borderRadius: RADII.control, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                  <input type="checkbox" checked={includeQuestions} onChange={e => setIncludeQuestions(e.target.checked)} />{' '}
                  Lead-form questions found — append them to Questions &amp; privacy
                </label>
                <div style={{ fontSize: '12px', color: INK.body, background: NEUTRAL.pageBg, borderRadius: RADII.small, padding: '8px 10px', maxHeight: '110px', overflowY: 'auto', lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: sanitizeCopyHtml(preview.questions_html) }} />
                {preview.questions_image_count > 0 && (
                  <div style={{ fontSize: '11.5px', color: STATE.gate.text }}>
                    ⚠ The questions section holds {preview.questions_image_count} embedded
                    image{preview.questions_image_count === 1 ? '' : 's'} — answers supplied as
                    images can't be read as text, so they're missing above. Add them by hand on
                    Questions &amp; privacy after the import.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => { setPhase('pick'); setPreview(null); setErr(''); }} style={buttonSmall}>← Different file</button>
              <div style={{ flexGrow: 1 }} />
              <span style={{ fontSize: '12px', color: INK.muted }}>
                {importCount} of {blocks.length} block{blocks.length === 1 ? '' : 's'} will import
              </span>
              <button onClick={apply} disabled={applyDisabled}
                style={applyDisabled ? { ...buttonPrimary(), ...buttonDisabled } : buttonPrimary()}>
                {busy ? 'Applying…' : 'Apply import'}
              </button>
            </div>
          </>
        )}

        {phase === 'done' && result && (
          <>
            <div style={{ fontSize: '13.5px', fontWeight: 700, color: STATE.done.deep }}>
              ✓ Imported copy onto {result.applied} asset{result.applied === 1 ? '' : 's'}
              {result.questions_appended ? ' · questions appended to Questions & privacy' : ''}
            </div>
            {(result.created_assets || []).length > 0 && (
              <div style={{ fontSize: '12.5px', color: INK.body }}>
                New linked assets: {result.created_assets.map(a => a.title).join(', ')}
              </div>
            )}
            {(result.skipped || []).length > 0 && (
              <div style={{ fontSize: '12px', color: INK.secondary, lineHeight: 1.6 }}>
                Skipped:
                <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                  {result.skipped.map((s, i) => <li key={i}>{s.title ? `${s.title} — ` : ''}{s.reason}</li>)}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={buttonPrimary()}>Done</button>
            </div>
          </>
        )}

        {err && <div style={{ fontSize: '12.5px', color: STATE.danger.text }}>{err}</div>}
      </div>
    </div>
  );
}
